import React from "react";
import { useTranslation } from "react-i18next";
import "./disclaimerCard.css";
import { Modal } from "../modals/Modal";
import useFormFactor from "../../hooks/useFormFactor";
import { useBranding } from "../../branding/useBranding";

interface ConsentFormProps {
  handleConsentChange: (isChecked: boolean) => void;
  email: string;
  closeModal: () => void;
  gaveConsent: boolean;
}

export default function ConsentForm({
  handleConsentChange,
  email,
  closeModal,
  gaveConsent,
}: ConsentFormProps) {
  const { t } = useTranslation();
  const { formFactor } = useFormFactor();
  const branding = useBranding();

  const disclosureTranslations = {
    consentTitle: t("immunizations.search_consent_title"),
    consentButton: t("immunizations.i_consent").replace(".", ""),
    prominantDisclosure: t("immunizations.prominant_disclosure"),
    consentCheckBoxLabel: t("immunizations.i_consent"),
    mobileHeader: t("immunizations.get_started_header"),
    mobileButton: t("immunizations.get_started_button"),
    mobileDescription: t("immunizations.consent_copy"),
  };

  return (
    <>
      <Modal
        id="consent-modal"
        open={formFactor === "desktop"}
        title={disclosureTranslations.consentTitle}
        hasClose={false}
        data-testid="consent-modal"
        actionButtons={
          <button
            className={"btn docket-button pl-4 pr-4"}
            onClick={closeModal}
            disabled={!gaveConsent}
          >
            {disclosureTranslations.consentButton.toUpperCase()}
          </button>
        }
        onCloseModal={closeModal}
      >
        <p className="mb-5 text-black dark:text-white">
          {disclosureTranslations.prominantDisclosure}
        </p>
        <label className="fieldset-label">
          <input
            type="checkbox"
            data-testid="consent-checkbox-desktop"
            name={disclosureTranslations.consentCheckBoxLabel}
            checked={gaveConsent}
            onChange={(e) => handleConsentChange(e.target.checked)}
            className="checkbox"
          />
          <span className="text-black dark:text-white">
            {disclosureTranslations.consentCheckBoxLabel}
          </span>
        </label>
      </Modal>
      <dialog id="consent-form-mobile" className="modal md:hidden" open>
        <div className="modal-box h-full w-full rounded-none mobile-background dark:!bg-dark flex flex-col">
          <div className="flex flex-col docket-gap-2">
            <img
              className="dark:hidden mx-auto h-12"
              src={`/${branding.mobileHeaderImage}`}
              alt="Docket logo"
            />
            <img
              className="hidden dark:inline mx-auto h-12"
              src={`/${branding.mobileHeaderImageHighContrast}`}
              alt="Docket logo"
            />
            <div className="flex flex-col docket-gap-1">
              <p className="mobile-text-color text-3xl font-bold">
                {disclosureTranslations.mobileHeader}
              </p>
              <p className="mobile-text-color text-sm">
                {disclosureTranslations.mobileDescription}
              </p>
            </div>
          </div>
          <div className="flex flex-col docket-gap-2 mt-auto">
            <label className="fieldset-label pl-6">
              <input
                type="checkbox"
                name={disclosureTranslations.consentCheckBoxLabel}
                checked={gaveConsent}
                onChange={(e) => handleConsentChange(e.target.checked)}
                className="checkbox checked:text-white mobile-checkbox mr-2"
              />
              <span className="mobile-text-color">
                {disclosureTranslations.consentCheckBoxLabel}
              </span>
            </label>
            <button
              className={
                "btn w-full py-6 disabled:!bg-gray-300 disabled:text-gray-400 text-docket-primary dark:bg-white"
              }
              onClick={closeModal}
              disabled={!gaveConsent}
            >
              {disclosureTranslations.mobileButton}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
